import { AccordionSmall, EmissionList, Formheader } from "@/Components";
import {
  chartColors,
  chartTextColors,
  DashboardBarChart,
  DashboardPieChart,
} from "@/Utilities/V2Graphs";
import { convertWeightToHumanReadable, sum } from "@/Utilities/Utilities";
import { useMemo, useState } from "react";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { useLoading } from "@/Contexts/LoadingContext";
import Placeholder from "@/Components/Placeholder";
import { IconListUl, IconPizzaSlice } from "@/Icons";
import { usePage, useRemember } from "@inertiajs/inertia-react";
import ScientificUncertaintyRating from "@/Components/Dashboard/ScientificUncertaintyRating";
import { getChartColor } from "@/Utilities/V2Graphs";

const EmissionGraphPerScope = ({ entries, labels }) => {
  const aggregatedScientificUncertainty =
    usePage().props?.aggregated_scientific_uncertainty || [];

  const { isLoaded } = useLoading();

  const DISPLAY = {
    PIE: "pie",
    LIST: "list",
  };

  const [displayMode, setDisplayMode] = useRemember(
    DISPLAY.PIE,
    "Chart/Scopes/DisplayMode",
  );
  const [scopeFilter, setScopeFilter] = useState(
    SCOPE_OPTIONS.map((scope) => scope.value),
  );

  const dataSetsPerScope = useMemo(
    () =>
      SCOPE_OPTIONS.map((scope, i) => {
        if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
          return;
        }
        return {
          label: scope.label,
          data: labels.map((label) => {
            return Object.keys(entries.teams).reduce((acc, team_id) => {
              return (
                acc +
                (entries.teams[team_id].data[label.key]?.[scope.value] || 0)
              );
            }, 0);
          }),
          barPercentage: 0.5,
          backgroundColor: getChartColor(i, scope.label),
          textColor: chartTextColors[i],
        };
      })?.filter((dataSet) => dataSet) || [], // filter out undefined values
    [entries, scopeFilter],
  );

  const pieData =
    dataSetsPerScope.map((dataSet) =>
      dataSet.data.reduce((a, b) => a + b, 0),
    ) || [];

  const aggregatedScientificUncertaintySum = {
    high: 0,
    low: 0,
  };

  const scopeData =
    dataSetsPerScope.map((dataSet, scopeIndex) => {
      const data = dataSet.data.map((value, i) => {
        const set = {
          y: value,
        };

        if (scopeIndex === 0) {
          const total = dataSetsPerScope.reduce((acc, dataSet) => {
            return acc + dataSet.data[i];
          }, 0);

          const uncertaintyPercentage =
            aggregatedScientificUncertainty?.[labels[i].key];
          if (uncertaintyPercentage) {
            set.yMin = total * (1 - uncertaintyPercentage);
            set.yMax = total * (1 + uncertaintyPercentage);

            aggregatedScientificUncertaintySum.high += set.yMax;
            aggregatedScientificUncertaintySum.low += set.yMin;
          }
        }

        return set;
      });

      return {
        ...dataSet,
        data,
      };
    }) || [];

  const getQuestionsEmissionByScope = (scope) => {
    return Object.keys(entries.questions)
      .map((question_id) => {
        const question = entries.questions[question_id];

        return {
          name: question.label,
          value: question.data?.[scope] || 0,
        };
      })
      .filter((question) => question.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  return (
    <>
      <div className="bg-gray-light px-8 pb-8 pt-4">
        <div className="mb-2 flex flex-wrap gap-4  max-xl:mb-4">
          <Formheader margin="max-md:mb-0 my-4 max-md:w-full">
            Emissions to air during this period
          </Formheader>
          {SCOPE_OPTIONS.map((scope, i) => {
            return (
              <div
                key={i}
                className="flex cursor-pointer items-center gap-2"
                onClick={() => {
                  if (scopeFilter.includes(scope.value)) {
                    setScopeFilter(
                      scopeFilter.filter((s) => s !== scope.value),
                    );
                  } else {
                    setScopeFilter([...scopeFilter, scope.value]);
                  }
                }}
              >
                <span
                  className={`h-3 w-3 rounded-[2px] border-[1px] border-white outline outline-1 ${
                    scopeFilter.includes(scope.value) ? "" : "bg-white"
                  }`}
                  style={{
                    backgroundColor: scopeFilter.includes(scope.value)
                      ? getChartColor(i, scope.label)
                      : "",
                    outlineColor: getChartColor(i, scope.label),
                  }}
                ></span>
                <span>{scope.label}</span>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col justify-between gap-8 xl:flex-row">
          <div className="flex w-full shrink-0 flex-col items-start justify-start gap-4 border-misty-dawn max-xl:border-b-[1px] max-xl:pb-8 xl:w-2/5 xl:border-r-[1px] xl:pr-8">
            <p>Total emissions</p>
            <div
              className={`w-full ${
                displayMode === DISPLAY.PIE ? "block" : "hidden"
              }`}
            >
              {isLoaded ? (
                <div className="mb-4 flex flex-col">
                  <h4 className="large-number mb-4">
                    {pieData.length
                      ? convertWeightToHumanReadable(sum(pieData))?.concat
                      : "0 kg"}{" "}
                    CO₂e
                  </h4>
                  <div className="high-low-indicator">
                    <p>
                      High:{" "}
                      <span className="font-medium">
                        {
                          convertWeightToHumanReadable(
                            aggregatedScientificUncertaintySum.high,
                          )?.concat
                        }{" "}
                        CO₂e
                      </span>
                    </p>
                    <p>
                      Low:{" "}
                      <span className="font-medium">
                        {
                          convertWeightToHumanReadable(
                            aggregatedScientificUncertaintySum.low,
                          )?.concat
                        }{" "}
                        CO₂e
                      </span>
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Placeholder width="350px" height="45px" className="mb-6" />
                  <Placeholder width="200px" height="20px" className="mb-2" />
                  <Placeholder width="150px" height="20px" className="mb-4" />
                </>
              )}
              {isLoaded ? (
                <DashboardPieChart dataSets={dataSetsPerScope} />
              ) : (
                <Placeholder
                  width="350px"
                  height="350px"
                  borderRadius="100%"
                  className="mb-[50px]"
                />
              )}
            </div>
            <div
              className={`w-full flex-col items-stretch justify-center gap-1  ${
                displayMode === DISPLAY.LIST ? "flex" : "hidden"
              }`}
            >
              {SCOPE_OPTIONS.map((scope, i) => {
                if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
                  return null;
                }

                return (
                  <EmissionsAccordionComponent
                    key={i}
                    title={scope.label}
                    isLoaded={isLoaded}
                    items={
                      isLoaded ? getQuestionsEmissionByScope(scope.value) : []
                    }
                  />
                );
              })}
            </div>

            <div className="flex w-full justify-end gap-2">
              <button
                className={`shadow-small p-2 ${
                  displayMode === DISPLAY.PIE
                    ? "bg-primary text-white"
                    : "bg-white"
                }`}
                onClick={() => setDisplayMode(DISPLAY.PIE)}
              >
                <IconPizzaSlice size={24} />
              </button>
              <button
                className={`shadow-small p-2 ${
                  displayMode === DISPLAY.LIST
                    ? "bg-primary text-white"
                    : "bg-white"
                }`}
                onClick={() => setDisplayMode(DISPLAY.LIST)}
              >
                <IconListUl size={24} />
              </button>
            </div>
          </div>
          <div className="flex w-full flex-col items-start xl:w-3/5">
            <p>Emissions per Scope</p>

            <div className="w-full grow">
              {isLoaded ? (
                <DashboardBarChart
                  dataSets={scopeData}
                  labels={labels.map((l) => l.name)}
                />
              ) : (
                <Placeholder width="100%" height="350px" />
              )}
            </div>

            {isLoaded ? (
              <ScientificUncertaintyRating
                scientificUncertainties={aggregatedScientificUncertainty}
              />
            ) : (
              <Placeholder width="100%" height="88px" />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const EmissionsAccordionComponent = ({ title, isLoaded, items = [] }) => {
  if (!items.length) return null;

  // calculate total from items
  const total = useMemo(() => sum(items.map((item) => item.value)), [items]);

  return (
    <AccordionSmall
      title={title}
      byline={
        isLoaded ? (
          `${convertWeightToHumanReadable(total)?.concat} CO₂e`
        ) : (
          <Placeholder width="100px" height="20px" />
        )
      }
    >
      <EmissionList items={items} total={total} />
    </AccordionSmall>
  );
};

export default EmissionGraphPerScope;
